import { DestroyRef, Injectable, computed, effect, inject, signal, untracked } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Permissions } from '@app/core/guard/Permissions';
import {
  IncomeTestAdjustmentRequest,
  PartnerAssetUpdateRequest,
} from '@interfaces/partner-income-tax/requests/partner-income-tax.request';
import {
  PartnerIncomeTaxOverviewDto,
  PartnerIncomeTaxPartnerDto,
} from '@interfaces/partner-income-tax/responses/partner-income-tax.response';
import { RealtimeEventTypes } from '@interfaces/realtime/events';
import { PermissionsService } from '@services/helpers/permissions.service';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { PartnerIncomeTaxService } from '@services/http/partner-income-tax/partner-income-tax.service';
import { RealtimeListRefreshService } from '@services/realtime/realtime-list-refresh.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subscription, finalize, map, of, catchError, first } from 'rxjs';

/** Sócio aberto na URL (`?partner={personId}` ou `?partner=consolidated`), para mandar o link de um sócio. */
export const PARTNER_INCOME_TAX_QUERY_PARAM = 'partner';
export const PARTNER_INCOME_TAX_CONSOLIDATED_PARAM = 'consolidated';

/** O que está aberto na aba: um sócio (`personId`) ou a visão consolidada. */
export type PartnerIncomeTaxSelection =
  | { kind: 'partner'; personId: string }
  | { kind: 'consolidated' };

/** Mensagem do backend (`ResultObject` com erro) ou o texto de reserva para falha técnica. */
export function describeApiError(error: unknown, fallback: string): string {
  const errors = (error as { errors?: { message?: string }[] } | null)?.errors;
  const messages = (errors ?? []).map(item => item?.message).filter((m): m is string => !!m);
  return messages.length ? messages.join(' ') : fallback;
}

/**
 * Estado da aba IR dos sócios (card 3515). Provido pelo `DfpComponent` (junto com o
 * `RealtimeListRefreshService`), porque a própria tela precisa do número de sócios para o contador da aba antes
 * de a aba ser aberta. O componente da aba só lê e chama os métodos daqui.
 *
 * Tempo real: `partner-income-tax.changed` no tópico automático `company:{id}` relê a visão geral e o sócio
 * aberto, sem polling (evento é dica, REST é a verdade).
 */
@Injectable()
export class PartnerIncomeTaxStateService {
  private readonly http = inject(PartnerIncomeTaxService);
  private readonly toast = inject(ToastHelperService);
  private readonly realtimeRefresh = inject(RealtimeListRefreshService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly permissions = inject(PermissionsService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  /** Grava o sócio aberto no `?partner=` (fora do modo embutido, onde a URL é de quem embute). */
  private urlSync = false;

  /** A aba lê e grava a URL (`?partner=`, `?section=`): falso no modo embutido. */
  get syncsUrl(): boolean {
    return this.urlSync;
  }
  /** Sócio pedido pela URL, aberto já na primeira carga (em paralelo com a visão geral). */
  private requestedSelection: PartnerIncomeTaxSelection | null = null;

  /** `PARTNERINCOMETAX_VIEW`: sem ela a aba não existe e nada é pedido ao backend. */
  readonly canView = toSignal(this.permissions.hasPermission(Permissions.PartnerIncomeTax.VIEW), {
    initialValue: false,
  });
  /** `PARTNERINCOMETAX_UPDATE`: ajustar teste de renda, editar bem, dispensar ponto, exigência de garantias. */
  readonly canUpdate = toSignal(
    this.permissions.hasPermission(Permissions.PartnerIncomeTax.UPDATE),
    { initialValue: false }
  );

  /** Ligado pelo `DfpComponent`: fora do modo somente leitura. */
  private readonly enabled = signal(false);
  readonly companyId = signal<string | null>(null);

  readonly overview = signal<PartnerIncomeTaxOverviewDto | null>(null);
  readonly overviewLoading = signal(false);
  readonly overviewError = signal<string | null>(null);

  readonly selection = signal<PartnerIncomeTaxSelection | null>(null);
  readonly partner = signal<PartnerIncomeTaxPartnerDto | null>(null);
  readonly partnerLoading = signal(false);
  readonly partnerError = signal<string | null>(null);
  /** Ano-calendário escolhido no histórico; `null` = o mais recente (padrão do backend). */
  readonly selectedYear = signal<number | null>(null);

  /** Alguma gravação em andamento (desabilita botões de ação). */
  readonly saving = signal(false);

  /** A aba existe para este usuário nesta tela. */
  readonly available = computed(() => this.enabled() && this.canView());
  readonly partnerCount = computed(() => this.overview()?.partners.length ?? 0);

  private overviewSub: Subscription | null = null;
  private partnerSub: Subscription | null = null;

  constructor() {
    // Empresa ou permissão mudou: (re)carrega a visão geral. A permissão chega assíncrona; o effect espera.
    effect(() => {
      const companyId = this.companyId();
      const available = this.available();
      untracked(() => {
        if (companyId && available) this.loadOverview();
      });
    });

    this.realtimeRefresh
      .refresh$({
        eventType: RealtimeEventTypes.PartnerIncomeTaxChanged,
        topicMatches: topic => topic.startsWith('company:'),
        shouldRefresh: envelope => {
          const current = this.companyId()?.toLowerCase();
          const eventCompany = (
            envelope.companyId ?? (envelope.payload?.['companyId'] as string | undefined)
          )?.toLowerCase();
          return !!current && current === eventCompany;
        },
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.reload(true));
  }

  setEnabled(enabled: boolean): void {
    this.enabled.set(enabled);
  }

  /**
   * Liga o `?partner=` (rota da Análise Econômica; embutida, a URL é do deal). Lê o sócio da URL uma vez: ele é aberto
   * já na primeira carga, junto com a visão geral, em vez de esperar a visão geral voltar.
   */
  initUrlSync(enabled: boolean): void {
    this.urlSync = enabled;
    if (!enabled) return;
    const value = this.route.snapshot?.queryParamMap?.get(PARTNER_INCOME_TAX_QUERY_PARAM)?.trim();
    if (!value) return;
    this.requestedSelection =
      value === PARTNER_INCOME_TAX_CONSOLIDATED_PARAM
        ? { kind: 'consolidated' }
        : { kind: 'partner', personId: value };
  }

  /** Troca de empresa zera tudo; a carga sai pelo effect. */
  setCompany(companyId: string | null): void {
    if (companyId === this.companyId()) return;
    // O sócio da URL vale só para a empresa com que a tela abriu.
    if (this.companyId()) this.requestedSelection = null;
    this.overviewSub?.unsubscribe();
    this.partnerSub?.unsubscribe();
    this.overview.set(null);
    this.overviewError.set(null);
    this.partner.set(null);
    this.partnerError.set(null);
    this.selection.set(null);
    this.selectedYear.set(null);
    this.companyId.set(companyId);
  }

  /** Releitura (tempo real, botão "Tentar de novo"). `silent` não mostra o carregando de novo. */
  reload(silent = false): void {
    if (!this.companyId() || !this.available()) return;
    this.loadOverview(silent);
  }

  selectPartner(personId: string): void {
    const current = this.selection();
    if (current?.kind === 'partner' && current.personId === personId) return;
    this.selection.set({ kind: 'partner', personId });
    this.selectedYear.set(null);
    this.partner.set(null);
    this.writeSelectionToUrl(personId);
    this.loadPartner();
  }

  selectConsolidated(): void {
    this.partnerSub?.unsubscribe();
    this.selection.set({ kind: 'consolidated' });
    this.writeSelectionToUrl(PARTNER_INCOME_TAX_CONSOLIDATED_PARAM);
  }

  /** Só nas escolhas do usuário: a seleção automática do primeiro sócio não suja a URL. */
  private writeSelectionToUrl(value: string): void {
    if (!this.urlSync) return;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { [PARTNER_INCOME_TAX_QUERY_PARAM]: value },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  selectYear(calendarYear: number): void {
    if (calendarYear === this.partner()?.selectedCalendarYear) return;
    this.selectedYear.set(calendarYear);
    this.loadPartner();
  }

  adjustIncomeTest(returnId: string, request: IncomeTestAdjustmentRequest): void {
    this.savePartner(
      this.http.adjustIncomeTest(returnId, request),
      'Não conseguimos gravar o ajuste do teste de renda. O valor na tela não foi salvo; saia do campo de novo para tentar.'
    ).subscribe();
  }

  restoreIncomeTest(returnId: string): void {
    this.savePartner(
      this.http.restoreIncomeTest(returnId),
      'Não conseguimos restaurar os valores da declaração. Nada mudou; use Restaurar de novo.',
      'Valores da declaração restaurados.'
    ).subscribe();
  }

  /** Devolve `true` quando gravou, para a modal fechar. */
  updateAsset(assetId: string, request: PartnerAssetUpdateRequest): Observable<boolean> {
    return this.savePartner(
      this.http.updateAsset(assetId, request),
      'Não conseguimos salvar o bem. Nada foi alterado; confira os campos e salve de novo.',
      'Bem atualizado. Os indicadores e os pontos de atenção já foram recalculados.'
    );
  }

  dismissFinding(pointId: string, reason: string): Observable<boolean> {
    const partner = this.partner();
    if (!partner?.selectedCalendarYear) return of(false);
    return this.savePartner(
      this.http.dismissFinding(partner.personId, pointId, {
        calendarYear: partner.selectedCalendarYear,
        reason,
      }),
      'Não conseguimos dispensar o ponto. Ele continua na lista; tente de novo.',
      'Ponto dispensado. Ele sai dos principais e continua em "Ver mais pontos".'
    );
  }

  undoDismissFinding(pointId: string): void {
    const partner = this.partner();
    if (!partner?.selectedCalendarYear) return;
    this.savePartner(
      this.http.undoDismissFinding(partner.personId, pointId, partner.selectedCalendarYear),
      'Não conseguimos reativar o ponto. Ele continua dispensado; tente de novo.',
      'Ponto reativado.'
    ).subscribe();
  }

  /** Pede a leitura de novo das declarações da empresa (após falha). */
  reprocess(): void {
    const companyId = this.companyId();
    if (!companyId) return;
    this.saving.set(true);
    this.http
      .reprocess(companyId)
      .pipe(
        first(),
        finalize(() => this.saving.set(false))
      )
      .subscribe({
        next: response => {
          const queued = response?.queued ?? 0;
          this.toast.showToastSuccess(
            queued
              ? `${queued} ${queued === 1 ? 'documento voltou' : 'documentos voltaram'} para a leitura. A tela se atualiza sozinha quando terminar.`
              : 'Nenhum documento precisava ser lido de novo.'
          );
          this.reload(true);
        },
        error: error =>
          this.toast.showToastError(
            describeApiError(
              error,
              'Não conseguimos pedir a leitura de novo. Tente em alguns instantes.'
            )
          ),
      });
  }

  /** `reloadPartner`: numa releitura silenciosa, relê também o sócio aberto (falso depois de uma gravação). */
  private loadOverview(silent = false, reloadPartner = silent): void {
    const companyId = this.companyId();
    if (!companyId) return;
    this.overviewSub?.unsubscribe();
    if (!silent) this.overviewLoading.set(true);
    this.overviewError.set(null);
    this.applyRequestedSelection();
    this.overviewSub = this.http
      .getOverview(companyId)
      .pipe(
        first(),
        finalize(() => this.overviewLoading.set(false))
      )
      .subscribe({
        next: overview => {
          if (companyId !== this.companyId()) return;
          this.overview.set(overview);
          this.reconcileSelection(overview, reloadPartner);
        },
        error: error => {
          if (silent && this.overview()) return;
          this.overviewError.set(
            describeApiError(
              error,
              'Não conseguimos carregar o IR dos sócios agora. Use Tentar de novo; se continuar, avise o time de tecnologia.'
            )
          );
        },
      });
  }

  /**
   * Sócio da URL na primeira carga: pede o sócio junto com a visão geral (antes era uma cascata, o sócio só saía
   * depois de a visão geral voltar). Se ele não existir mais, `reconcileSelection` abre o primeiro.
   */
  private applyRequestedSelection(): void {
    const requested = this.requestedSelection;
    if (!requested || this.selection()) return;
    this.requestedSelection = null;
    this.selection.set(requested);
    if (requested.kind === 'partner') this.loadPartner();
  }

  /** Mantém o sócio aberto quando ele ainda existe; senão abre o primeiro. Relê o sócio numa releitura. */
  private reconcileSelection(
    overview: PartnerIncomeTaxOverviewDto | null,
    reloadPartner: boolean
  ): void {
    const partners = overview?.partners ?? [];
    const current = this.selection();
    if (current?.kind === 'consolidated' && partners.length) return;
    if (current?.kind === 'partner' && partners.some(p => p.personId === current.personId)) {
      if (reloadPartner) this.loadPartner(true);
      return;
    }
    if (partners.length) {
      this.selection.set({ kind: 'partner', personId: partners[0].personId });
      this.selectedYear.set(null);
      this.loadPartner();
    } else {
      this.selection.set(null);
      this.partner.set(null);
    }
  }

  private loadPartner(silent = false): void {
    const companyId = this.companyId();
    const selection = this.selection();
    if (!companyId || selection?.kind !== 'partner') return;
    this.partnerSub?.unsubscribe();
    if (!silent) this.partnerLoading.set(true);
    this.partnerError.set(null);
    this.partnerSub = this.http
      .getPartner(companyId, selection.personId, this.selectedYear())
      .pipe(
        first(),
        finalize(() => this.partnerLoading.set(false))
      )
      .subscribe({
        next: partner => this.partner.set(partner),
        error: error => {
          if (silent && this.partner()) return;
          this.partnerError.set(
            describeApiError(
              error,
              'Não conseguimos abrir este sócio agora. Escolha o sócio de novo para tentar outra vez.'
            )
          );
        },
      });
  }

  /**
   * Gravação que devolve o sócio recalculado: troca o sócio na tela e relê a visão geral em silêncio (os cards e
   * o consolidado dependem do mesmo número). Erro de negócio mostra a mensagem do backend.
   */
  private savePartner(
    request$: Observable<PartnerIncomeTaxPartnerDto | null>,
    fallbackError: string,
    successMessage?: string
  ): Observable<boolean> {
    this.saving.set(true);
    return request$.pipe(
      first(),
      map(partner => {
        if (partner) this.partner.set(partner);
        if (successMessage) this.toast.showToastSuccess(successMessage);
        this.loadOverview(true, false);
        return true;
      }),
      catchError(error => {
        this.toast.showToastError(describeApiError(error, fallbackError));
        return of(false);
      }),
      finalize(() => this.saving.set(false))
    );
  }
}
